import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ColumnsContainer, DeviceSettingsButton, DollarSignIcon, getDeviceStatusText, LinkIcon, PopupContentContainer, ReportIcon, RowsContainer, SingleRowButtonWithIcon, Toggle, } from '@streamr/tv-ui';
import cx from 'classnames';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { COMPLAINT_REPORT_URL } from '../../consts';
import { useShare } from '../../hooks/useShare';
import { BroadcastService } from '../../services/broadcast/BroadcastService';
import { MediaService } from '../../services/media/MediaService';
import { BottomRight, Popup } from '../../utils/popups';
import { formBroadcastUrl } from '../../utils/url';
import { Anchor } from '../Anchor';
import { PopupRecordButton } from '../broadcaster/helpers/PopupRecordButton';
import { AbstractPopup, DismissalReason, } from './AbstractPopup';
import { selectCamera } from './devices/CameraSelectorPopup';
import { selectMicrophone } from './devices/MicrophoneSelectorPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { showAudienceContributionsSettingsPopup } from './merchant/AudienceContributionsSettingsPopup';
import { PopupCssPreset } from './Popup.styles';
export function BroadcastPopup({ reject, broadcast, initialQuality, onVideoQualityChanged, variant, ...props }) {
    const { canShare, openSystemShareDialog } = useShare();
    const selectedVideoDevice = MediaService.getOutput().useSelectedVideoDevice();
    const selectedAudioDevice = MediaService.getOutput().useSelectedAudioDevice();
    const isVideoDisabled = MediaService.getOutput().useVideoMuted();
    const isAudioDisabled = MediaService.getOutput().useAudioMuted();
    const videoPermissioned = MediaService.getOutput().useCameraPermissioned();
    const audioPermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const noAudioDevices = MediaService.getOutput().useAudioDevices().length === 0;
    const noVideoDevices = MediaService.getOutput().useVideoDevices().length === 0;
    const quality = BroadcastService.getOutput().useQuality();
    // Quality now reflects service state; changes are notified to the service directly
    function isValidQuality(value) {
        return value === 'sd' || value === 'hd' || value === 'uhd';
    }
    const microphoneStatusText = useMemo(() => getDeviceStatusText(selectedAudioDevice, noAudioDevices, audioPermissioned), [selectedAudioDevice, noAudioDevices, audioPermissioned]);
    const cameraStatusText = useMemo(() => getDeviceStatusText(selectedVideoDevice, noVideoDevices, videoPermissioned), [selectedVideoDevice, noVideoDevices, videoPermissioned]);
    const microphoneButtonAnchorRef = useRef(null);
    const [microphoneSelectorOpen, setMicrophoneSelectorOpen] = useState(false);
    const cameraButtonAnchorRef = useRef(null);
    const [cameraSelectorOpen, setCameraSelectorOpen] = useState(false);
    return (_jsx(BroadcastPopupRoot, { reject: (reason) => {
            if (microphoneSelectorOpen || cameraSelectorOpen) {
                return;
            }
            reject(reason);
        }, translateFn: variant === 'desktop' ? BottomRight : undefined, ...props, className: cx(props.className, variant), children: _jsx(ScrollablePopupContainer, { fullscreenOnly: variant === 'mobile', onCloseButtonClick: () => {
                reject(DismissalReason.CloseButton);
            }, children: _jsxs(BroadcastPopupContent, { children: [canShare && (_jsx(SingleRowButtonWithIcon, { label: "Share link", icon: _jsx(LinkIcon, {}), onClick: () => {
                            openSystemShareDialog(formBroadcastUrl(broadcast));
                        } })), _jsx(SingleRowButtonWithIcon, { label: "Audience contributions", icon: _jsx(DollarSignIcon, {}), onClick: () => {
                            reject();
                            showAudienceContributionsSettingsPopup({
                                anchorRef: props.anchorRef,
                                variant: variant === 'desktop'
                                    ? 'top-right'
                                    : 'bottom-right',
                            });
                        } }), _jsxs(ColumnsContainer, { children: [_jsx(Anchor, { ref: microphoneButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "microphone", label: "Microphone", statusText: microphoneStatusText, isActive: !isAudioDisabled, displayErrorIcon: !audioPermissioned || noAudioDevices, onClick: () => {
                                        selectMicrophone({
                                            anchorRef: microphoneButtonAnchorRef,
                                            variant: 'bottom-center',
                                            onOpenPopup: () => {
                                                setMicrophoneSelectorOpen(true);
                                            },
                                            onClosePopup: () => {
                                                setMicrophoneSelectorOpen(false);
                                            },
                                        });
                                    } }) }), _jsx(Anchor, { ref: cameraButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "camera", label: "Camera", statusText: cameraStatusText, isActive: !isVideoDisabled, displayErrorIcon: !videoPermissioned || noVideoDevices, onClick: () => {
                                        selectCamera({
                                            anchorRef: cameraButtonAnchorRef,
                                            variant: 'bottom-center',
                                            onOpenPopup: () => {
                                                setCameraSelectorOpen(true);
                                            },
                                            onClosePopup: () => {
                                                setCameraSelectorOpen(false);
                                            },
                                        });
                                    } }) })] }), _jsx(PopupContentContainer, { children: _jsxs(RowsContainer, { children: [_jsx("span", { className: "tv-ui-font-medium", children: "Display quality" }), _jsx(Toggle, { options: ['sd', 'hd', 'uhd'], selected: quality, onChange: (newQuality) => {
                                        if (isValidQuality(newQuality)) {
                                            onVideoQualityChanged?.(newQuality);
                                        }
                                    } })] }) }), _jsxs(ColumnsContainer, { children: [_jsx(PopupRecordButton, { anchorRef: props.anchorRef, variant: variant, closePopup: reject, broadcast: broadcast }), _jsx(StyledButton, { type: "button", onClick: () => {
                                    window.open(COMPLAINT_REPORT_URL, '_blank');
                                }, children: _jsxs(ButtonContent, { children: [_jsx(ReportIcon, {}), _jsx("span", { children: "Report" })] }) })] }), _jsxs(RecordingDisclaimer, { children: [_jsx("strong", { children: "CAUTION:" }), _jsx("br", {}), "Recording is a beta feature. ", _jsx("br", {}), "It's currently centralized and unencrypted."] })] }) }) }));
}
const BroadcastPopupContent = styled(RowsContainer) `
    width: 100%;
    gap: 1rem;
`;
const BroadcastPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    &.desktop {
        @media (min-width: 30rem) {
            ${PopupCssPreset.AnchoredBottomRight};
            --Popup_Width: 25rem;
            --Popup_Padding: 1rem 0;
        }
    }

    &.mobile {
        --SettingsContainer_InnerMaxWidth: 25rem;
    }
`;
const StyledButton = styled(Button) `
    --Button_Transform: translate(0);
    --Button_HoverTransform: translate(0);
    --Button_ActiveTransform: translate(0);
    --Button_HoverBackground: inherit;
`;
const ButtonContent = styled(PopupContentContainer) `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;
const RecordingDisclaimer = styled.div `
    font-size: 0.875rem;
    color: var(--tv-ui-color-2);
    text-align: center;
    line-height: 1.25rem;

    strong {
        font-weight: 600;
    }
`;
const broadcastPopup = Popup.create(BroadcastPopup);
export function showBroadcastPopup({ anchorRef, variant, onBeforeOpen, onDismiss, broadcast, initialQuality, onVideoQualityChanged, }) {
    (async () => {
        try {
            onBeforeOpen?.();
            await broadcastPopup.pop({
                anchorRef,
                variant,
                broadcast,
                initialQuality,
                onVideoQualityChanged,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    })();
}
